import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import {
  TEXT_TO_VIDEO_FREE_TRIAL_PRESET,
  VideoAspectRatio,
  VideoGenerationMode,
  VideoGenerationModel,
  VideoResolution,
} from '@/ai/video-models';
import { getGrokVideoGeneratorSeoData } from '@/components/custom/grok-video-generator-seo-data';
import { GrokVideoGeneratorJsonLd } from '@/components/custom/grok-video-generator-json-ld';
import { GrokVideoGeneratorSeoContent } from '@/components/custom/grok-video-generator-seo-content';
import { TextToVideoPage } from '@/components/custom/text-to-video-page';
import { WorkbenchVideoDemo } from '@/components/custom/workbench-video-demo';
import { seo } from '@/lib/seo';

const searchSchema = z.object({
  prompt: z.string().optional(),
  model: z
    .enum(
      Object.values(VideoGenerationModel) as [
        VideoGenerationModel,
        ...VideoGenerationModel[],
      ]
    )
    .optional(),
  aspectRatio: z
    .enum(
      Object.values(VideoAspectRatio) as [
        VideoAspectRatio,
        ...VideoAspectRatio[],
      ]
    )
    .optional(),
  duration: z.coerce.number().int().positive().optional(),
  resolution: z
    .enum(
      Object.values(VideoResolution) as [VideoResolution, ...VideoResolution[]]
    )
    .optional(),
  mode: z
    .enum([
      VideoGenerationMode.NORMAL,
      VideoGenerationMode.FUN,
      VideoGenerationMode.SPICY,
    ])
    .optional(),
  generateAudio: z.enum(['1']).optional(),
  parentGenerationId: z.string().min(1).optional(),
  continuationType: z.enum(['continue', 'variation', 'retry']).optional(),
});

export const Route = createFileRoute('/text-to-video')({
  validateSearch: searchSchema,
  head: () => {
    const data = getGrokVideoGeneratorSeoData();
    return seo(data.path, {
      title: data.seoTitle,
      description: data.seoDescription,
      keywords: data.keywords,
    });
  },
  component: GrokVideoGeneratorRoute,
});

function GrokVideoGeneratorRoute() {
  const search = Route.useSearch();
  return (
    <>
      <TextToVideoPage
        emptyPreview={
          <WorkbenchVideoDemo
            ariaLabel="Text-to-video portrait performance demo"
            badge="Text to video demo"
            poster="/text-to-video/text-to-video-portrait-performance-scene-poster.jpg"
            src="/text-to-video/text-to-video-portrait-performance-scene.mp4"
            testId="text-to-video-demo"
          />
        }
        seoContent={<GrokVideoGeneratorSeoContent />}
        initialPrompt={search.prompt}
        initialModel={search.model ?? TEXT_TO_VIDEO_FREE_TRIAL_PRESET.model}
        initialAspectRatio={search.aspectRatio}
        initialDuration={search.duration}
        initialResolution={search.resolution}
        initialMode={search.mode}
        initialGenerateAudio={search.generateAudio === '1'}
        promptOptimizationProfile="realistic-video"
        parentGenerationId={search.parentGenerationId}
        continuationType={search.continuationType}
      />
      <GrokVideoGeneratorJsonLd />
    </>
  );
}
